import * as React from "react";
import { Navigate, useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { CheckCircle2, GraduationCap, Loader2 } from "lucide-react";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Input,
  Label,
  LoadingScreen,
} from "@eduviaos/ui-kit";
import {
  ApiError,
  type AcceptStaffInviteResponse,
  type StaffInviteContext,
  type TenantVerticalLiteral,
} from "@eduviaos/shared";
import { api } from "@/lib/api";
import { useAuth } from "@/auth/AuthProvider";
import { termsApiFor } from "@/hooks/useTerms";

const PASSWORD_MIN = 10;

const ROLE_LABELS: Record<string, string> = {
  teacher: "Teacher",
  staff: "Staff",
  admin: "Administrator",
  owner: "Owner",
};

/**
 * Phase C1b — public staff-invite accept page.
 *
 * Sibling to `AcceptInvitePage` (the principal-invite redeem flow) but
 * scoped to the smaller staff-invite contract: the User row already
 * carries role / branch / designation, so the recipient just sets a
 * password. On success we plant the access token in the AuthProvider
 * and route to ``/dashboard`` — same landing as the principal flow so
 * the IA SPA's existing AppShell takes over (and its first-run gate
 * does not fire for staff users since their tenant has already been
 * activated by an admin).
 */
export function StaffInviteAcceptPage() {
  const params = useParams<{ token: string }>();
  const token = params.token ?? "";
  const navigate = useNavigate();
  const { setSession, markFirstRunComplete, status } = useAuth();

  const contextQuery = useQuery({
    queryKey: ["onboarding", "staff-invite", token],
    queryFn: () => api.get<StaffInviteContext>(`/onboarding/staff-invite/${token}`),
    retry: false,
    enabled: token.length > 0 && status !== "authed",
  });

  if (status === "authed") {
    // Authed users have no business on the public accept page.
    return <Navigate to="/dashboard" replace />;
  }

  if (token.length === 0) {
    return <ErrorView title="Invite link is missing the token." />;
  }

  if (contextQuery.isPending) {
    return (
      <LoadingScreen
        label="Loading invite…"
        className="from-background via-background to-muted/40 bg-gradient-to-b py-12"
      />
    );
  }

  if (contextQuery.error instanceof ApiError) {
    return <ErrorView title={titleForCode(contextQuery.error.code)} />;
  }

  if (!contextQuery.data) {
    return <ErrorView title="This invite couldn't be loaded." />;
  }

  return (
    <ActivateForm
      ctx={contextQuery.data}
      token={token}
      onSuccess={(resp) => {
        // The principal-invite flow's setSession hard-codes
        // first_run_complete=False (correct for THAT case — a freshly
        // minted tenant). Staff users redeem AFTER the principal has
        // completed the wizard, so we immediately flip the local flag
        // back to true via markFirstRunComplete to skip the wizard gate
        // — same effect as polling /auth/me but without the round-trip.
        setSession({
          user: resp.user,
          access_token: resp.tokens.access_token,
        });
        markFirstRunComplete();
        navigate("/dashboard", { replace: true });
      }}
    />
  );
}

function ActivateForm({
  ctx,
  token,
  onSuccess,
}: {
  ctx: StaffInviteContext;
  token: string;
  onSuccess: (resp: AcceptStaffInviteResponse) => void;
}) {
  const [password, setPassword] = React.useState("");
  const [confirm, setConfirm] = React.useState("");
  const [clientError, setClientError] = React.useState<string | null>(null);

  const acceptMutation = useMutation({
    mutationFn: (newPassword: string) =>
      api.post<AcceptStaffInviteResponse>(`/onboarding/staff-invite/${token}/accept`, {
        password: newPassword,
      }),
    onSuccess,
  });

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setClientError(null);
    if (password.length < PASSWORD_MIN) {
      setClientError(`Password must be at least ${PASSWORD_MIN} characters.`);
      return;
    }
    if (password !== confirm) {
      setClientError("Passwords don't match.");
      return;
    }
    acceptMutation.mutate(password);
  };

  const serverError =
    acceptMutation.error instanceof ApiError
      ? extractServerErrorMessage(acceptMutation.error)
      : null;

  const vertical: TenantVerticalLiteral =
    ctx.tenant_type === "study_centre" || ctx.tenant_type === "coaching_institute"
      ? ctx.tenant_type
      : "school";
  const roleLabel = termsApiFor(vertical).roleLabel(
    ctx.role,
    ROLE_LABELS[ctx.role] ?? "Team member",
  );

  return (
    <main className="from-background via-background to-muted/40 grid min-h-svh place-items-center bg-gradient-to-b px-4 py-12">
      <div className="grid w-full max-w-md gap-6">
        <header className="grid gap-4 text-center">
          <div className="bg-card text-muted-foreground mx-auto inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-xs uppercase tracking-[0.18em]">
            <span className="bg-primary/15 text-primary grid size-5 place-items-center rounded-full">
              <GraduationCap className="size-3.5" aria-hidden="true" />
            </span>
            {roleLabel} invite
          </div>
          <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">
            Join {ctx.tenant_name}
          </h1>
          <p className="text-muted-foreground text-sm">
            You've been invited to join{" "}
            <strong className="text-foreground font-medium">{ctx.tenant_name}</strong> as{" "}
            <strong className="text-foreground font-medium">{roleLabel}</strong>
            {ctx.designation ? <> ({ctx.designation})</> : null}. Set a password to activate your
            account.
          </p>
        </header>

        <Card>
          <CardHeader>
            <CardTitle>Activate your account</CardTitle>
            <CardDescription>
              Signing in as <span className="text-foreground font-medium">{ctx.email}</span>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="grid gap-4">
              <div className="grid gap-1.5">
                <Label htmlFor="full-name">Your name</Label>
                <Input
                  id="full-name"
                  value={ctx.full_name}
                  readOnly
                  aria-readonly
                  className="bg-muted/40"
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  required
                  autoFocus
                  autoComplete="new-password"
                  minLength={PASSWORD_MIN}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder={`At least ${PASSWORD_MIN} characters`}
                />
              </div>
              <div className="grid gap-1.5">
                <Label htmlFor="confirm">Confirm password</Label>
                <Input
                  id="confirm"
                  type="password"
                  required
                  autoComplete="new-password"
                  minLength={PASSWORD_MIN}
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                />
              </div>

              {(clientError || serverError) && (
                <div
                  role="alert"
                  className="border-destructive/50 bg-destructive/5 text-destructive rounded-md border px-3 py-2 text-sm"
                >
                  {clientError ?? serverError}
                </div>
              )}

              <Button type="submit" disabled={acceptMutation.isPending} className="mt-1">
                {acceptMutation.isPending ? (
                  <>
                    <Loader2 className="size-4 animate-spin" aria-hidden="true" />
                    Activating…
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="size-4" aria-hidden="true" />
                    Set password & sign in
                  </>
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        <p className="text-muted-foreground text-center text-xs">
          Trouble with this link? Ask your administrator to reissue it.
        </p>
      </div>
    </main>
  );
}

function ErrorView({ title }: { title: string }) {
  return (
    <main className="bg-muted/30 grid min-h-svh place-items-center px-4 py-12">
      <Card className="max-w-md text-center">
        <CardHeader>
          <CardTitle>{title}</CardTitle>
        </CardHeader>
      </Card>
    </main>
  );
}

function titleForCode(code: string): string {
  switch (code) {
    case "INVITE_ALREADY_USED":
      return "This invite has already been used.";
    case "INVITE_EXPIRED":
      return "This invite has expired.";
    case "NOT_FOUND":
      return "This invite link is no longer valid.";
    default:
      return "We couldn't load this invite.";
  }
}

function extractServerErrorMessage(err: ApiError): string {
  switch (err.code) {
    case "VALIDATION_ERROR":
      return err.message || "Password didn't meet the requirements.";
    case "NOT_FOUND":
      return "This invite is no longer available. Ask your administrator to reissue.";
    case "INVITE_ALREADY_USED":
      return "This invite has already been used. Try signing in directly.";
    case "INVITE_EXPIRED":
      return "This invite has expired. Ask your administrator to reissue.";
    default:
      return err.message || "Could not activate your account. Try again in a moment.";
  }
}
